import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { isNavigationFailure } from 'vue-router';

import BlockViewer from '@/components/BlockViewer.vue';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';

import '@/assets/tailwind.css';
import '@/assets/styles.scss';

import * as Sentry from '@sentry/vue';

const app = createApp(App);

function isChunkLoadError(error) {
    return (
        error instanceof Error &&
        (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed'))
    );
}

app.config.errorHandler = (err, instance, info) => {
    if (isNavigationFailure(err)) {
        return;
    }

    if (isChunkLoadError(err)) {
        return; // Handled by router.onError with page reload
    }

    if (!(err instanceof Error)) {
        const original = err;
        err = new Error(typeof original === 'string' ? original : 'Non-Error thrown: ' + JSON.stringify(original));
        err.originalValue = original;
    }
    Sentry.captureException(err, { extra: { info } });
    console.error(err);
};

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENV,
    sendDefaultPii: true,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false
        })
    ],
    tracesSampleRate: 0.02,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
        const error = hint?.originalException;

        // Filter out EventSource Event objects (SSE onerror passes Event, not Error)
        if (error instanceof Event && !(error instanceof ErrorEvent)) {
            return null;
        }

        // Filter out ad-blocker errors for analytics/tracking scripts
        if (error instanceof Error && /analytics|gtag|firebase.*analytics/i.test(error.message)) {
            return null;
        }

        // Filter out stale chunk errors (expected after deployments, handled by router reload)
        if (isChunkLoadError(error)) {
            return null;
        }

        // Enrich events that have no useful message
        if (event.exception?.values) {
            for (const val of event.exception.values) {
                if (!val.value || val.value === 'No error message' || val.value === 'undefined') {
                    val.value = error?.message || error?.toString?.() || 'Unknown error (no message)';
                }
            }
        }

        return event;
    }
});
app.use(router);

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}'
        },
        overlay: {
            modal: {
                borderRadius: '1.5rem'
            },
            popover: {
                borderRadius: '10px'
            }
        },
        colorScheme: {
            light: {
                surface: {
                    0: 'color-mix(in srgb, {primary.950}, white 100%)',
                    50: 'color-mix(in srgb, {primary.950}, white 95%)',
                    100: 'color-mix(in srgb, {primary.950}, white 90%)',
                    200: 'color-mix(in srgb, {primary.950}, white 80%)',
                    300: 'color-mix(in srgb, {primary.950}, white 70%)',
                    400: 'color-mix(in srgb, {primary.950}, white 60%)',
                    500: 'color-mix(in srgb, {primary.950}, white 50%)',
                    600: 'color-mix(in srgb, {primary.950}, white 40%)',
                    700: 'color-mix(in srgb, {primary.950}, white 30%)',
                    800: 'color-mix(in srgb, {primary.950}, white 20%)',
                    900: 'color-mix(in srgb, {primary.950}, white 10%)',
                    950: 'color-mix(in srgb, {primary.950}, white 5%)'
                }
            },
            dark: {
                surface: {
                    0: 'color-mix(in srgb, var(--surface-ground), white 100%)',
                    50: 'color-mix(in srgb, var(--surface-ground), white 95%)',
                    100: 'color-mix(in srgb, var(--surface-ground), white 90%)',
                    200: 'color-mix(in srgb, var(--surface-ground), white 80%)',
                    300: 'color-mix(in srgb, var(--surface-ground), white 70%)',
                    400: 'color-mix(in srgb, var(--surface-ground), white 60%)',
                    500: 'color-mix(in srgb, var(--surface-ground), white 50%)',
                    600: 'color-mix(in srgb, var(--surface-ground), white 40%)',
                    700: 'color-mix(in srgb, var(--surface-ground), white 30%)',
                    800: 'color-mix(in srgb, var(--surface-ground), white 20%)',
                    900: 'color-mix(in srgb, var(--surface-ground), white 10%)',
                    950: 'color-mix(in srgb, var(--surface-ground), white 5%)'
                }
            }
        }
    }
});

import { firebaseApp, auth } from './firebaseInit';

app.use(PrimeVue, {
    theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: '.app-dark'
        }
    }
});
app.use(ToastService);
app.use(ConfirmationService);

app.component('BlockViewer', BlockViewer);

app.mount('#app');
